import type { CityConfig } from "../city-template/types";

export const rochesterMnConfig: CityConfig = {
  city: "Rochester",
  state: "Minnesota",
  stateAbbr: "MN",
  slug: "rochester-mn",
  route: "/cities/rochester-mn",
  title: "Emergency & Fast Passport Renewal Rochester MN | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Rochester, MN — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Olmsted County and Mayo Clinic area travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Rochester, MN",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Rochester and Mayo Clinic area travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Rochester%20Minnesota%20skyline%20with%20modern%20medical%20buildings%20and%20Mayo%20Clinic%20campus%2C%20clean%20urban%20architecture%20with%20green%20parks%2C%20warm%20golden%20afternoon%20light%2C%20professional%20cityscape%20photography%20with%20warm%20earth%20tones%20and%20modern%20aesthetic%2C%20no%20readable%20text%20or%20people%2C%20inviting%20and%20clean%20atmosphere&width=1800&height=700&seq=rochester-mn-hero-001&orientation=landscape",
  heroImageAlt: "Rochester Minnesota skyline with Mayo Clinic campus and modern buildings",
  geoPosition: "44.0121;-92.4802",
  geoPlacename: "Rochester, Minnesota",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, Chicago, IL 60605",
    distance: "Chicago is 5–6 hours by car from Rochester — our courier eliminates the need to drive",
  },
  nearbyCitiesZips: [
    { name: "Downtown Rochester", zips: "55902, 55904" },
    { name: "Mayo Clinic Area", zips: "55902, 55905" },
    { name: "Northwest Rochester", zips: "55901" },
    { name: "Southeast Rochester", zips: "55904, 55906" },
    { name: "Stewartville", zips: "55976" },
    { name: "Kasson", zips: "55944" },
    { name: "Byron", zips: "55920" },
    { name: "Chatfield", zips: "55923" },
    { name: "Plainview", zips: "55964" },
    { name: "Zumbrota", zips: "55992" },
    { name: "Red Wing", zips: "55066" },
    { name: "Winona", zips: "55987" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Mayo Clinic area departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Minneapolis", route: "/cities/minneapolis" },
    { name: "Emergency passport help in St. Paul", route: "/cities/st-paul" },
    { name: "Urgent passport services in Duluth", route: "/cities/duluth-mn" },
    { name: "Expedited passport services in Chicago", route: "/states/illinois" },
    { name: "Passport courier services in Iowa", route: "/states/iowa" },
    { name: "Rush passport help in Madison WI", route: "/states/illinois" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Rochester remotely via secure courier. We do not have a walk-in office in Rochester — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Mayo Clinic area travelers.",
  fpcOfficeDistance: "Serving all of Olmsted County and southeastern Minnesota",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire Rochester area and southeastern Minnesota. From the Mayo Clinic campus to the surrounding communities, from downtown Rochester to the Mississippi River valley — we help southeastern Minnesota travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Rochester neighborhoods and nearby communities including Stewartville, Kasson, Byron, Chatfield, Plainview, and Red Wing. If you are in Olmsted County or southeastern Minnesota, we have you covered.",
  howItWorksIntro: "Most Rochester travelers complete everything online and ship documents overnight. No need to drive to Chicago or the Twin Cities. Our emergency and expedited passport process is designed for southeastern Minnesota travelers who need fast, reliable results.",
  mistakesIntro: "With Rochester being a major medical and international research hub, Mayo Clinic area travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Rochester travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Rochester? Here are the most common ones we hear from Mayo Clinic area travelers.",
  statePageRoute: "/states/minnesota",
  statePageName: "Minnesota",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Rochester international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Dr. Patel",
      location: "Mayo Clinic Area, Rochester, MN",
      rating: 5,
      text: "My conference in Tokyo was in 4 days and I realized my passport was expired. Fast Passport Center got me a new one in 48 hours. Saved my medical research presentation completely.",
      service: "Emergency Passport",
    },
    {
      name: "Karen M.",
      location: "Stewartville, MN",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Costa Rica. The process was smooth, they reviewed my documents, and I had my passport in 5 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Northwest Rochester, MN",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Spain. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Susan T.",
      location: "Kasson, MN",
      rating: 5,
      text: "As a Mayo Clinic nurse who travels for mission work, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "roc-1",
      name: "Rochester Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1401 3rd Ave SE",
      city: "Rochester",
      state: "MN",
      zip: "55904",
      phone: "(507) 285-8971",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "roc-2",
      name: "Rochester Northwest Station",
      type: "USPS Passport Acceptance Facility",
      address: "3001 Wellner Dr NE",
      city: "Rochester",
      state: "MN",
      zip: "55906",
      phone: "(507) 288-9623",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "roc-3",
      name: "Stewartville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "105 1st Ave SE",
      city: "Stewartville",
      state: "MN",
      zip: "55976",
      phone: "(507) 533-8481",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for south metro residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "roc-4",
      name: "Kasson City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "607 W Main St",
      city: "Kasson",
      state: "MN",
      zip: "55944",
      phone: "(507) 634-3611",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "15 miles from downtown Rochester",
      website: "https://www.kassonmn.gov/",
      websiteLabel: "Kasson City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "roc-5",
      name: "Red Wing Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "222 Bush St",
      city: "Red Wing",
      state: "MN",
      zip: "55066",
      phone: "(651) 388-3011",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "45 miles from downtown Rochester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mississippi River valley residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "roc-6",
      name: "Olmsted County Service Center",
      type: "County Clerk Passport Acceptance",
      address: "151 4th St SE",
      city: "Rochester",
      state: "MN",
      zip: "55904",
      phone: "(507) 328-7650",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from downtown Rochester",
      website: "https://www.olmstedcounty.gov/",
      websiteLabel: "Olmsted County",
      notes: "Major county facility. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5+ hours to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Rochester photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rochester acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Rochester applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Rochester families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Mayo Clinic area travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Rochester." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Rochester address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Rochester?",
      a: "Fast Passport Center offers emergency passport processing in Rochester in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Mayo Clinic area travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Rochester?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to Rochester is in Chicago — a 5–6 hour drive. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Rochester residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Rochester?",
      a: "Fast Passport Center offers fast passport renewal in Rochester with no office visit required. We serve all Olmsted County neighborhoods including the Mayo Clinic area, downtown Rochester, Stewartville, and Kasson. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Rochester?",
      a: "The fastest way to get an urgent passport in Rochester is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in Rochester?",
      a: "With Fast Passport Center's emergency courier service, Rochester travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit a passport agency in person?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Rochester?",
      a: "Popular options include the Rochester Main Post Office (55904), Rochester Northwest Station (55906), Stewartville Post Office (55976), and Olmsted County Service Center (55904). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in a nearby town like Stewartville or Kasson?",
      a: "We serve all of Olmsted County and surrounding communities including Stewartville, Kasson, Byron, Chatfield, and Red Wing. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Rochester?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Rochester?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Rochester offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Rochester?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Rochester?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Rochester families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Rochester", address: "4601 Maine Ave SE, Rochester, MN 55904", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Rochester", address: "2020 2nd St SW, Rochester, MN 55902", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Rochester", address: "1201 S Broadway, Rochester, MN 55904", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Rochester", address: "1611 2nd St SW, Rochester, MN 55902", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Rochester", address: "3827 Marketplace Dr NW, Rochester, MN 55901", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Rochester", address: "910 15th Ave SE, Rochester, MN 55904", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Stewartville", address: "1050 S Main St, Stewartville, MN 55976", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Red Wing", address: "2955 Tyler Rd, Red Wing, MN 55066", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Rochester",
      address: "151 4th St SE, Rochester, MN 55904",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Northwest Rochester",
      address: "3001 Wellner Dr NE, Rochester, MN 55906",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Stewartville",
      address: "105 1st Ave SE, Stewartville, MN 55976",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Red Wing",
      address: "2006 Commerce Dr, Red Wing, MN 55066",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Mississippi River valley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "RST",
      name: "Rochester International Airport",
      distance: "8 miles from downtown Rochester",
      address: "7600 Helgerson Dr SW, Rochester, MN 55902",
      note: "The primary airport for Rochester and southeastern Minnesota. Offers domestic flights and international connections via major hubs.",
      flightUrl: "https://www.flyrst.com/",
      flightLabel: "RST Flight Info",
      featured: true,
    },
    {
      code: "MSP",
      name: "Minneapolis–Saint Paul International Airport",
      distance: "80 miles from Rochester",
      address: "4300 Glumack Dr, St. Paul, MN 55111",
      note: "Major international hub for Rochester travelers connecting through MSP.",
      flightUrl: "https://www.mspairport.com/",
      flightLabel: "MSP Flight Info",
      featured: false,
    },
  ],
};